import type { AuthStatus } from "../../services/authService";

const STORAGE_KEY = "authStatusCache";
const CACHE_VERSION = 1;
const CACHE_TTL_MS = 5 * 60 * 1000;

type AuthCachePayload = {
  version: number;
  timestamp: number;
  status: AuthStatus;
};

function sanitizeAuthStatus(status: AuthStatus): AuthStatus {
  return {
    isAuthenticated: status.isAuthenticated,
    username: status.username,
    userId: status.userId,
    loginId: status.loginId,
    attributes: status.attributes,
    profile: status.profile ?? null,
  };
}

export function readAuthCache(): AuthStatus | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthCachePayload;
    if (!parsed || parsed.version !== CACHE_VERSION) return null;
    if (typeof parsed.timestamp !== "number") return null;
    if (Date.now() - parsed.timestamp > CACHE_TTL_MS) return null;
    if (!parsed.status || typeof parsed.status !== "object") return null;
    return parsed.status as AuthStatus;
  } catch {
    return null;
  }
}

export function writeAuthCache(status: AuthStatus): void {
  try {
    const payload: AuthCachePayload = {
      version: CACHE_VERSION,
      timestamp: Date.now(),
      status: sanitizeAuthStatus(status),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage failures
  }
}

export function clearAuthCache(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage failures
  }
}
